import React, { useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18next';
import './login.css';
import LogoFormContainer from './LogoFormContainer';
import {
  MButton,
  BufferService,
  LoadingContext,
  DefaultFunctionURL,
} from 'src/M_Components';

// Auth providers + storage keys
import { useUserAuth } from 'src/components/auth/UserAuthProvider';
import { useCarrierAuth } from 'src/components/auth/CarrierAuthProvider';
import { STORAGE_USER, STORAGE_CARRIER } from 'src/components/auth/storageKeys';

/**
 * Key used in localStorage to persist the pending invite context
 * created by CarrierInviteEntryPage.
 *
 * Shape example:
 * {
 *   type: "spot" | "phase",
 *   tenderId: number | null,
 *   tenderPhaseId: number | null,
 *   token: string,
 *   createdAt: number
 * }
 *
 * The actual consumption of this context (navigation to CarrierInviteViewBid)
 * is now handled inside the carrier realm shell (CarrierHomePage), not here.
 */
const PENDING_INVITE_KEY = 'carrier_invite_pending';

const INVITE_TARGET_ROUTE = '/carrier/home/CarrierInviteViewBid'

/**
 * Login component
 * -----------------------------------------------------------------------------
 * Works for two realms:
 *
 * - realm="user"
 *   * Calls /api/Identity/login
 *   * Stores user JWT + menus + permissions into STORAGE_USER keys
 *   * Updates UserAuthProvider state
 *   * Redirects to "#/home"
 *
 * - realm="carrier"
 *   * Calls /api/CarrierIdentity/login
 *   * Stores carrier JWT + menus + permissions + profile into STORAGE_CARRIER keys
 *   * Updates CarrierAuthProvider state
 *   * After login, always redirects to "#/carrier/home".
 *   * If there is a pending invite (carrier_invite_pending), CarrierHomePage
 *     will detect it and route to CarrierInviteViewBid.
 */
export default function Login({ realm = 'user' }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);

  // Hooks used to update auth state (no API here, just token/state updates)
  const userAuth = useUserAuth();
  const carrierAuth = useCarrierAuth();

  const [emailOrUserName, setEmailOrUserName] = useState('');
  const [password, setPassword] = useState('');

  // Base API URL + login BufferService based on realm
  const baseUrl =
    DefaultFunctionURL?.() || process.env.REACT_APP_API_BASE_URL || '';
  const loginService = useMemo(
    () =>
      new BufferService(
        realm === 'carrier' ? 'CarrierIdentity/login' : 'Identity/login',
        baseUrl
      ),
    [realm, baseUrl]
  );

  /**
   * After a successful carrier login, decide where to go.
   *
   * Behaviour:
   *  - If there is a valid pending invite (carrier_invite_pending),
   *    redirect directly to the internal invite wrapper:
   *      "#/carrier/home/CarrierInviteViewBid?...".
   *    This avoids race conditions with TabsHomePage and CarrierHomePage
   *    initial routing logic and guarantees the invite flow resumes correctly.
   *
   *  - If there is no pending invite (or it is invalid),
   *    redirect to the normal carrier home:
   *      "#/carrier/home".
   *
   * Notes:
   *  - This function does NOT consume (remove) the pending invite from storage.
   *    Cleanup is intentionally delegated to the invite flow (home/wrapper),
   *    providing extra safety in case of refreshes or edge cases.
   */
  const redirectCarrierAfterLogin = () => {
    try {
      const raw = window.localStorage.getItem(PENDING_INVITE_KEY);

      if (raw) {
        const ctxInvite = JSON.parse(raw);

        // Basic validation of the invite context 
        const isValid =
          ctxInvite &&
          typeof ctxInvite.token === 'string' &&
          ctxInvite.token.length > 0 &&
          (ctxInvite.tenderId || ctxInvite.tenderPhaseId);

        if (isValid) {
          // Build the same query string used by the invite entry flow
          const searchParams = new URLSearchParams();

          if (ctxInvite.type) {
            searchParams.set('type', String(ctxInvite.type));
          }
          if (ctxInvite.tenderId) {
            searchParams.set('tenderId', String(ctxInvite.tenderId));
          }
          if (ctxInvite.tenderPhaseId) {
            searchParams.set('tenderPhaseId', String(ctxInvite.tenderPhaseId));
          }

          searchParams.set('token', String(ctxInvite.token));

          // Redirect straight to the invite wrapper inside the carrier realm
          window.location.href =
            `#${INVITE_TARGET_ROUTE}?${searchParams.toString()}`;
          return;

        }
      }
    } catch (e) {
      // Ignore malformed storage or JSON parsing errors
    }

    // Default carrier flow (no invite pending)
    window.location.href = '#/carrier/home';
  };


  // =============================================================================
  // Handle login submit (decides payload/endpoints by realm)
  // =============================================================================
  const handleLoginSubmit = () => {
    // Basic client-side validation
    if (!emailOrUserName || !password) {
      ctx.toastMessages('error', t('LabelTryAgain'), '');
      return;
    }

    // Map request payload depending on API contract
    // Currently both realms use { username, password }
    const body =
      realm === 'carrier'
        ? { username: emailOrUserName, password }
        : { username: emailOrUserName, password };

    ctx.setIsLoad(true);

    loginService
      .post(body)
      .then((response) => {
        ctx.setIsLoad(false);

        // Axios-style error payload (BufferService usually normalizes,
        // but we keep this defensive check)
        if (response?.name === 'AxiosError') {
          const msg =
            response?.response?.data ?? t('LabelTryAgain'); // fallback generic message
          ctx.toastMessages('error', msg, '');
          return;
        }

        // ---------------------------------------------------------------------
        // REALM: USER
        // ---------------------------------------------------------------------
        if (realm === 'user') {
          // If the API returns no Token, user must confirm email first (existing behavior)
          if (!response?.Token) {
            ctx.toastMessages('success', '', t('LabelNeedConfirmEmail'));
            return;
          }

          // Persist using new realm-scoped keys
          try {
            localStorage.setItem(STORAGE_USER.token, response.Token);
            localStorage.setItem(
              STORAGE_USER.menus,
              JSON.stringify(response.Menus || [])
            );
            localStorage.setItem(
              STORAGE_USER.permissions,
              JSON.stringify(response.Permissions || [])
            );
            localStorage.setItem(
              "permissions",
              JSON.stringify(response.Permissions || [])
            );
            localStorage.setItem("defaultAccountName", response.DefaultAccountName || "");
            localStorage.setItem("token", response.Token || "");
            localStorage.setItem("defaultShipperId", String(response.DefaultShipperId ?? ""));
            localStorage.setItem("defaultShipperName", response.DefaultShipperName ?? "");
            localStorage.setItem("userId", String(response.Id ?? ""));
            localStorage.setItem("cargofiveApiKeyId", String(response.CargofiveApiKeyId ?? ""));
            // Optional: store a compact profile payload
            localStorage.setItem(
              STORAGE_USER.profile,
              JSON.stringify({
                id: response.Id,
                userName: response.UserName,
                companyCode: response.CompanyCode,
                companyName: response.CompanyName,
                defaultAccountName: response.DefaultAccountName,
                defaultShipperId: response.DefaultShipperId,
                defaultShipperName: response.DefaultShipperName,
                cargofiveApiKeyId: response.CargofiveApiKeyId,
              })
            );
          } catch (_) {
            // Ignore storage errors (quota, private mode, etc.)
          }

          // Inform user AuthProvider so guards and UI react immediately
          userAuth.login(response.Token);

          // Sync i18n language preference
          i18n.changeLanguage(localStorage.getItem('lng') || 'pt');

          // UX feedback + redirect to user home
          ctx.toastMessages('success', '', t('AlertMessageSucessLogged'));
          window.location.href = '#/home';
          return;
        }

        // ---------------------------------------------------------------------
        // REALM: CARRIER
        // ---------------------------------------------------------------------
        // Expected response contains:
        //   Token, ExpiresUtc, Session, Account, Carrier, Menus, Permissions, PermissionsClaim
        if (!response?.Token) {
          ctx.toastMessages('error', t('LabelTryAgain'), '');
          return;
        }

        try {
          // Store with dedicated keys for the carrier realm
          localStorage.setItem(STORAGE_CARRIER.token, response.Token);
          if (response.ExpiresUtc) {
            localStorage.setItem(
              STORAGE_CARRIER.expiresUtc,
              response.ExpiresUtc
            );
          }
          localStorage.setItem(
            STORAGE_CARRIER.menus,
            JSON.stringify(response.Menus || [])
          );
          localStorage.setItem(
            STORAGE_CARRIER.permissions,
            JSON.stringify(response.Permissions || [])
          );
          localStorage.setItem(
            STORAGE_CARRIER.session,
            JSON.stringify(response.Session || null)
          );
          localStorage.setItem(
            STORAGE_CARRIER.profile,
            JSON.stringify({
              account: response.Account || null,
              carrier: response.Carrier || null,
              permissionsClaim: response.PermissionsClaim || null,
            })
          );
        } catch (_) {
          // Ignore storage errors; token is also in memory via AuthProvider
        }

        // Notify CarrierAuthProvider (stores token + optional expiry internally)
        carrierAuth.login(response.Token, response.ExpiresUtc || null);

        // Sync i18n language preference
        i18n.changeLanguage(localStorage.getItem('lng') || 'pt');

        // UX feedback
        ctx.toastMessages('success', '', t('AlertMessageSucessLogged'));

        // Decide where to go after carrier login
        redirectCarrierAfterLogin();
      })
      .catch((error) => {
        ctx.setIsLoad(false);
        ctx.toastMessages('error', t('LabelTryAgain'), '');
      });
  };

  // =============================================================================
  // Handle Enter key press (submit login on Enter)
  // =============================================================================
  const handleEnter = (e) => {
    if (e.key === 'Enter') handleLoginSubmit();
  };

  // =============================================================================
  // Render (same UI; only behavior changes by "realm")
  // =============================================================================
  return (
    <div className="login-page">
      {/* Left side with Devlop Logo */}
      <div className="login-side-left">
        <img
          src="./images/Logo-devlop.png"
          alt="logoDevlop"
          className="devlop-logo"
        />
      </div>

      {/* Center with Logo + Form container (login or forgot-password) */}
      <div className="login-side-center">
        <LogoFormContainer
          // Behavior handlers
          handleLoginSubmit={handleLoginSubmit}
          handleEnter={handleEnter}
          setEmail={setEmailOrUserName}
          setPassword={setPassword}
          // Realm so ForgotPasswordForm can use correct endpoint
          realm={realm}
          // i18n + loading context
          t={t}
          ctx={ctx}
        />
      </div>

      {/* Right side with social network buttons */}
      <div className="login-side-right">
        <div className="social-buttons-wrapper">
          {[
            {
              label: 'Devlop Facebook',
              link: 'https://www.facebook.com/devlop.systems',
              src: './icons/socialIcons/Facebook.svg',
            },
            {
              label: 'Devlop Instagram',
              link: 'https://www.instagram.com/devlop.systems/',
              src: './icons/socialIcons/Instagram.svg',
            },
            {
              label: 'Devlop Linkedin',
              link: 'https://www.linkedin.com/company/devlop-systems/mycompany/',
              src: './icons/socialIcons/Linkedin.svg',
            },
            {
              label: 'Devlop Youtube',
              link: 'https://www.youtube.com/@devlopsystems',
              src: './icons/socialIcons/Youtube.svg',
            },
          ].map((el) => (
            <div key={el.label} className="social-button-container">
              <MButton
                none
                backgroundColor="transparent"
                tooltip={el.label}
                icon={<img src={el.src} alt={el.label} />}
                onClick={() => window.open(el.link, '_blank')}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
